import { useTranslation } from '../../../../../shared/i18n';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Card, Checkbox, Collapse, Empty, Form, Modal, Select, Space, Spin, Tag, Tooltip, Typography, message } from 'antd';
import { EditOutlined, MailOutlined, ReloadOutlined } from '@ant-design/icons';
import domainContactsService from '../../../../services/domainContactsService';
import domainRegistrationService from '../../../../services/domainRegistrationService';
import registrarService from '../../../../services/registrarService';
import ContactFields, { fieldErrorsToForm, fromFormContact, toFormContact } from '../ContactFields';
import { formatDate } from '../../../../../shared/utils/date';
import { userPath } from '../../../../../shared/config/paths';
import CountryFlag from '../../../../../shared/components/CountryFlag';

const { Text } = Typography;

const OTHER_ROLES = [
    { key: 'admin', label: '管理员联系人' },
    { key: 'tech', label: '技术联系人' },
    { key: 'billing', label: '账单联系人' },
];

export const VerificationTag = ({ v }) => {
    const { t } = useTranslation();
    if (!v) return null;
    if (v.status === 'verified') return <Tag color="success">{t('Verified')}</Tag>;
    if (v.status === 'failed') return <Tag color="error">{t('Verification failed')}</Tag>;
    if (v.status === 'pending') {
        return (
            <Tooltip title={t('Click the verification link the registrar sent to the registrant email')}>
                <Tag color="warning">{v.deadline ? t('Pending · by {{date}}', { date: formatDate(v.deadline) }) : t('Verification pending')}</Tag>
            </Tooltip>
        );
    }
    return null;
};

const summary = (c) => {
    if (!c) return '';
    const name = `${c.first_name || ''} ${c.last_name || ''}`.trim();
    return [c.kind === 'organization' && c.company ? c.company : null, name].filter(Boolean).join(' · ');
};

const ContactSummary = ({ c }) => (
    <Space direction="vertical" size={2}>
        <Text strong>{summary(c)}</Text>
        <Text type="secondary">{[c.email, c.phone].filter(Boolean).join(' · ')}</Text>
        <Text type="secondary">
            <CountryFlag code={c.country} />{[c.address1, c.address2, c.city, c.state, c.postal_code, c.country].filter(Boolean).join(', ')}
        </Text>
    </Space>
);

/** Address-book picker that fills the given form path. */
const FillFromBook = ({ templates, onPick }) => {
    const { t } = useTranslation();
    return (
        <Select
            size="small"
            placeholder={t('Fill from address book')}
            style={{ width: '100%', maxWidth: 280, marginBottom: 12 }}
            value={null}
            options={templates.map((x) => ({ value: x.id, label: `${x.template_name}${x.email ? ` · ${x.email}` : ''}` }))}
            onChange={(id) => onPick(templates.find((x) => x.id === id))}
            notFoundContent={<Link to={userPath('/domain-registration/contact-templates')}>{t('Add in address book')}</Link>}
        />
    );
};

const DomainContacts = ({ domain }) => {
    const { t, i18n } = useTranslation();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [editOpen, setEditOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [templates, setTemplates] = useState([]);
    const [others, setOthers] = useState({ admin: false, tech: false, billing: false });
    const [advOpen, setAdvOpen] = useState([]);
    const [busy, setBusy] = useState('');
    const [wait, setWait] = useState(0);
    const [form] = Form.useForm();

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await domainContactsService.get(domain.id);
            if (r?.success) {
                setData(r.data);
                setWait(r.data?.verification?.resend_wait || 0);
            }
        } catch (e) {
            message.error(e.error || e.message || t('Failed to load'));
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [domain.id]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        if (wait <= 0) return undefined;
        const tm = setTimeout(() => setWait((w) => Math.max(0, w - 1)), 1000);
        return () => clearTimeout(tm);
    }, [wait]);

    const contacts = data?.contacts;
    const v = data?.verification;
    const editable = !!data?.editable;

    const openEdit = () => {
        const init = { registrant: toFormContact(contacts?.registrant) };
        const o = {};
        OTHER_ROLES.forEach(({ key }) => {
            const c = contacts?.[key];
            o[key] = !!c && !c.same_as_registrant;
            init[key] = toFormContact(o[key] ? c : {});
        });
        setOthers(o);
        setAdvOpen(Object.values(o).some(Boolean) ? ['other'] : []);
        form.resetFields();
        form.setFieldsValue(init);
        setEditOpen(true);
        if (templates.length === 0) {
            registrarService.getContactTemplates()
                .then((r) => setTemplates(Array.isArray(r?.data) ? r.data : []))
                .catch(() => setTemplates([]));
        }
    };

    const fill = (role, tpl) => {
        if (!tpl) return;
        form.setFieldsValue({ [role]: toFormContact(tpl) });
    };

    const save = async () => {
        let values;
        try {
            values = await form.validateFields();
        } catch (e) {
            const role = e?.errorFields?.[0]?.name?.[0];
            if (role && role !== 'registrant') setAdvOpen(['other']);
            return;
        }
        const payload = { registrant: fromFormContact(values.registrant) };
        OTHER_ROLES.forEach(({ key }) => {
            if (others[key]) payload[key] = fromFormContact(values[key]);
        });
        // Name / organisation / e-mail of the registrant = change of registrant (gTLD rule): confirm first
        const rawBefore = contacts?.registrant || {};
        // registrars echo an individual's name as "company" — not an organisation
        const before = rawBefore.kind === 'organization' ? rawBefore : { ...rawBefore, company: '' };
        const after = payload.registrant || {};
        const norm = (v) => String(v || '').trim().toLowerCase();
        const material = ['first_name', 'last_name', 'company', 'email'].some((k) => norm(before[k]) !== norm(after[k]));
        if (material) {
            const go = await new Promise((resolve) => Modal.confirm({
                title: t('Change the domain owner?'),
                content: t('Changing the registrant name, organisation or e-mail sends an approval e-mail to the old and new registrant. After approval the domain cannot be transferred to another registrar for 60 days.'),
                okText: t('Continue'),
                onOk: () => resolve(true),
                onCancel: () => resolve(false),
            }));
            if (!go) return;
        }
        setSaving(true);
        try {
            const r = await domainRegistrationService.updateContacts({ id: domain.id, contacts: payload });
            if (r?.success && r.pending_registrant_approval) {
                Modal.info({
                    title: t('Approval required'),
                    content: t('We sent an approval e-mail to the old and new registrant. The change takes effect once approved{{deadline}}.', { deadline: r.approval_deadline ? ` (${t('before {{date}}', { date: String(r.approval_deadline).slice(0, 10) })})` : '' }),
                });
                setEditOpen(false);
                load();
            } else if (r?.success) {
                message.success(t('Contact updated'));
                setEditOpen(false);
                load();
            } else {
                message.error(r?.error || t('Update failed'));
            }
        } catch (e) {
            const fe = fieldErrorsToForm(e?.field_errors, (role) => [role && role !== 'registrant' ? role : 'registrant'], { t, lang: i18n?.language, form });
            if (fe.length) {
                form.setFields(fe);
                if (fe.some((f) => f.name[0] !== 'registrant')) setAdvOpen(['other']);
            }
            message.error(e?.error || e?.message || t('Update failed'));
        } finally {
            setSaving(false);
        }
    };

    const resend = async () => {
        setBusy('resend');
        try {
            const r = await domainContactsService.resendVerification(domain.id);
            if (r?.success) {
                message.success(t('Verification email sent'));
                setWait(r.verification?.resend_wait || 600);
                setData((d) => ({ ...d, verification: r.verification || d.verification }));
            }
        } catch (e) {
            if (e?.retry_after) setWait(e.retry_after);
            message.error(e?.error || e?.message || t('Send failed'));
        } finally {
            setBusy('');
        }
    };

    const check = async () => {
        setBusy('check');
        try {
            const r = await domainContactsService.checkVerification(domain.id);
            if (r?.verification) setData((d) => ({ ...d, verification: r.verification }));
        } catch (e) {
            message.error(e?.error || e?.message || t('Operation failed'));
        } finally {
            setBusy('');
        }
    };

    const privacyOn = !!data?.privacy?.enabled;
    // 隐私模式：注册商登记的是平台代理，验证由平台处理
    const needsAction = !privacyOn && v && (v.status === 'pending' || v.status === 'failed');
    const otherItems = useMemo(() => OTHER_ROLES.map((r) => {
        const c = contacts?.[r.key];
        const same = !c || c.same_as_registrant;
        return { ...r, same, c };
    }), [contacts]);

    if (loading && !data) return <Spin />;
    if (!contacts) {
        return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('No contacts')} />;
    }

    return (
        <div style={{ maxWidth: 720 }}>
            <Card
                size="small"
                title={<Space size={8} wrap><span>{t('Registrant')}</span>{privacyOn
                    ? <Tooltip title={t('These are your real details, kept only on our platform; the registrar lists our proxy')}><Tag color="purple">{t('Privacy mode')}</Tag></Tooltip>
                    : <VerificationTag v={v} />}</Space>}
                extra={editable && <Button size="small" icon={<EditOutlined />} onClick={openEdit}>{t('Edit')}</Button>}
            >
                <div><ContactSummary c={contacts.registrant} /></div>
                {needsAction && editable && (
                    <Space wrap style={{ marginTop: 12 }}>
                        <Button size="small" icon={<MailOutlined />} loading={busy === 'resend'} disabled={wait > 0} onClick={resend}>
                            {wait > 0 ? t('Resend verification email ({{s}}s)', { s: wait }) : t('Resend verification email')}
                        </Button>
                        <Tooltip title={t('Clicked the link? Refresh status')}>
                            <Button size="small" type="text" icon={<ReloadOutlined />} loading={busy === 'check'} onClick={check} />
                        </Tooltip>
                    </Space>
                )}
            </Card>

            <Card size="small" style={{ marginTop: 12 }}>
                {otherItems.map((r, i) => (
                    <div key={r.key} style={{ display: 'flex', gap: 12, padding: '6px 0', borderTop: i ? '1px solid rgba(128,128,128,0.15)' : undefined, flexWrap: 'wrap' }}>
                        <Text type="secondary" style={{ minWidth: 88 }}>{t(r.label)}</Text>
                        {r.same ? <Text>{t('Same as registrant')}</Text> : (
                            <Text>{summary(r.c)}{r.c?.email ? <Text type="secondary"> · {r.c.email}</Text> : null}</Text>
                        )}
                    </div>
                ))}
            </Card>

            <Modal
                title={<Space direction="vertical" size={0}><span>{t('Edit contact')}</span><Text type="secondary" style={{ fontSize: 12, fontWeight: 'normal' }}>{t('Only changes this domain; synced to the registrar on save')}</Text></Space>}
                open={editOpen}
                onCancel={() => setEditOpen(false)}
                onOk={save}
                confirmLoading={saving}
                okText={t('Save')}
                width={640}
                forceRender
            >
                <Form form={form} layout="vertical" requiredMark={false}>
                    <FillFromBook templates={templates} onPick={(tpl) => fill('registrant', tpl)} />
                    <ContactFields prefix={['registrant']} compact />
                    <Collapse
                        ghost
                        activeKey={advOpen}
                        onChange={(k) => setAdvOpen(Array.isArray(k) ? k : [k])}
                        items={[{
                            key: 'other',
                            label: t('Use different details for admin/tech/billing contacts'),
                            forceRender: true,
                            children: OTHER_ROLES.map((r) => (
                                <div key={r.key} style={{ marginBottom: 12 }}>
                                    <Checkbox
                                        checked={!others[r.key]}
                                        onChange={(e) => setOthers((o) => ({ ...o, [r.key]: !e.target.checked }))}
                                    >
                                        {t('{{role}} same as registrant', { role: t(r.label) })}
                                    </Checkbox>
                                    {others[r.key] && (
                                        <div style={{ marginTop: 12, paddingLeft: 12, borderLeft: '2px solid rgba(128,128,128,0.2)' }}>
                                            <FillFromBook templates={templates} onPick={(tpl) => fill(r.key, tpl)} />
                                            <ContactFields prefix={[r.key]} compact />
                                        </div>
                                    )}
                                </div>
                            )),
                        }]}
                    />
                </Form>
            </Modal>
        </div>
    );
};

export default DomainContacts;
